/**
 * Per-move detail for the first-hour moves (C0-SKILLFX, audit in docs/research/C0_VFX.md §10.2): what each move adds
 * on top of its ink template — the art's element ramp and motif, the wind-up tell, a sub-frame smear, speed streaks
 * on fast moves, a body aura for buffs and heals, hit sparks by material, a ground mark, and the move's own extra
 * (a lifesteal stream, a pressure-point ripple, a travelling shockwave). Data only; drawn by `draw.ts`.
 */
import type { Motif } from './sprites';
import type { RampId } from './ramps';

export type Smear = 'arc' | 'rise' | 'line' | 'ring' | 'cross' | 'none';
export type Aura = 'rise' | 'swirl' | 'fan' | 'converge' | 'flame';
export type Spark = 'steel' | 'qi' | 'ink' | 'wind';
export type Mark = 'scuff' | 'scar' | 'ring' | 'stain' | 'none';

export interface MoveDetail {
  /** The element ramp and the art's motif (secondary particles), with an optional second motif. */
  ramp: RampId;
  motif: Motif;
  motifRamp?: RampId;
  alt?: Motif;
  /** The smear drawn on the frames between the wind-up and the contact. */
  smear: Smear;
  /** Speed streaks and a motif wake behind the body (fast moves); `dash`: along the path to the foe. */
  wake?: 'body' | 'dash';
  /** A ring smear and motifs flung round the caster (a spin). */
  spin?: boolean;
  /** A body aura over the move (buffs and heals): its shape. */
  aura?: Aura;
  spark: Spark;
  mark: Mark;
  /** Blood beads streaming from the foe back to the caster (lifesteal). */
  drain?: boolean;
  /** Pressure-point ripples on the foe (a sealing jab). */
  ripple?: boolean;
  /** A shockwave travelling from the caster to the foe before the blow (a ranged palm). */
  travel?: boolean;
  /** A ring of the element at the target spot (a ground-aimed area). */
  spot?: boolean;
  /** Blows after the first, in seconds after it (a chain: the smears alternate). */
  chain?: readonly number[];
}

const WIND = { ramp: 'wind', motif: 'leaf', motifRamp: 'leaf', spark: 'wind' } as const;
const CLOUD = { ramp: 'cloud', motif: 'puff', alt: 'fleck', spark: 'qi' } as const;
const BLOOD = { ramp: 'blood', motif: 'bead', spark: 'steel' } as const;
const INK = { ramp: 'ink', motif: 'bead', alt: 'fleck', spark: 'ink' } as const;

export const MOVE_DETAIL: Readonly<Record<string, MoveDetail>> = {
  // ── กระบี่ไผ่ห่มหมอก (ยอดหมอก, the starting art) ──
  'bs-gust': { ...WIND, smear: 'line', wake: 'body', mark: 'scuff' },
  'bs-cloud': { ...WIND, motif: 'puff', motifRamp: 'mist', smear: 'line', wake: 'body', mark: 'scar' },
  'bs-sweep': { ...WIND, smear: 'ring', spin: true, mark: 'scuff' },
  'bs-bamboo': { ...WIND, smear: 'none', aura: 'rise', mark: 'none' },
  'bs-glide': { ...WIND, smear: 'line', wake: 'dash', mark: 'scuff' },
  'bs-mist': { ...WIND, motif: 'mistPuff', motifRamp: 'mist', smear: 'none', aura: 'swirl', mark: 'none' },
  'bs-reed': { ...WIND, motif: 'fluff', motifRamp: 'mist', alt: 'leaf', smear: 'cross', wake: 'body', mark: 'scuff', chain: [0.15, 0.3] },
  // The giant sword is the spectacle; no leaf storm round the caster (QA VFX-R1 m2: the hero was buried).
  'bs-tempest': { ...WIND, smear: 'ring', mark: 'ring', chain: [0.15] },
  // ── ฝ่ามือเมฆาคล้อย (also คลื่นไผ่'s opening art) ──
  'cp-wave': { ...CLOUD, smear: 'line', mark: 'ring' },
  'cp-seize': { ...CLOUD, smear: 'line', mark: 'ring' },
  'cp-storm': { ...CLOUD, smear: 'ring', spin: true, mark: 'ring' },
  'cp-breath': { ...CLOUD, smear: 'none', aura: 'rise', mark: 'none' },
  'cp-drift': { ...CLOUD, motif: 'mistPuff', smear: 'none', aura: 'swirl', wake: 'body', mark: 'none' },
  'cp-roar': { ...CLOUD, smear: 'line', travel: true, mark: 'ring' },
  'cp-ring': { ...CLOUD, motifRamp: 'mist', smear: 'none', spot: true, mark: 'ring' },
  'cp-heaven': { ...CLOUD, smear: 'ring', spin: true, mark: 'ring' },
  // ── ดาบชาดคลั่งไฟ (ดาบชาด) ──
  'bs-slash': { ...BLOOD, smear: 'arc', mark: 'scar' },
  'bs-rend': { ...BLOOD, smear: 'cross', mark: 'scar', chain: [0.15] },
  'bs-whirl': { ...BLOOD, smear: 'ring', spin: true, mark: 'stain' },
  'bs-charge': { ...BLOOD, smear: 'line', wake: 'dash', mark: 'scuff' },
  'bs-break': { ...BLOOD, alt: 'fleck', smear: 'arc', mark: 'stain' },
  'bs-frenzy': { ...BLOOD, smear: 'none', aura: 'flame', mark: 'none' },
  'bs-drink': { ...BLOOD, smear: 'arc', drain: true, mark: 'scar' },
  'bs-massacre': { ...BLOOD, smear: 'ring', mark: 'stain' },
  // ── กระบี่แท่งหมึกสน (หอหมึกสน) ──
  'jz-stroke': { ...INK, smear: 'arc', mark: 'stain' },
  'jz-poem': { ...INK, smear: 'cross', mark: 'stain', chain: [0.15, 0.3] },
  'jz-ink': { ...INK, smear: 'ring', spin: true, mark: 'stain' },
  'jz-seal': { ...INK, smear: 'line', ripple: true, mark: 'none' },
  'jz-ward': { ...INK, smear: 'none', aura: 'fan', mark: 'none' },
  'jz-focus': { ...INK, smear: 'none', aura: 'converge', mark: 'none' },
  'jz-break': { ...INK, smear: 'arc', mark: 'stain' },
  'jz-scroll': { ...INK, smear: 'ring', mark: 'stain' },
};

export const detailOf = (skillId: string): MoveDetail | null => MOVE_DETAIL[skillId] ?? null;
